// frontend/packages/business-console/src/pages/Pulse/sections/KpiTile.jsx
//
// Compact "big number + label + optional delta" tile used inside Pulse
// section shells. Restyled to the console redesign type scale (900-weight
// numbers, token colors). Two layouts:
//   - Default: vertical stack (number above label).
//   - `inline`: number + label side-by-side (dense rows).
//
// `accent` accepts a token color or an MUI palette path (e.g.
// "success.main") for back-compat with existing callers.

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTokens } from '../../../components/primitives';

export default function KpiTile({
  value,
  label,
  sublabel,
  delta,
  inline,
  accent,
}) {
  const t = useTokens();
  const showDelta = typeof delta === 'number' && delta !== 0;
  const positive = delta > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: inline ? 'row' : 'column',
        alignItems: inline ? 'baseline' : 'flex-start',
        justifyContent: inline ? 'space-between' : 'flex-start',
        gap: inline ? 1 : 0.25,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: inline ? 18 : { xs: 22, sm: 26 },
          fontWeight: 900,
          lineHeight: 1.1,
          color: accent || t.text,
          letterSpacing: '-0.03em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography
          sx={{
            color: t.faint,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 700,
            fontSize: 11,
          }}
        >
          {label}
        </Typography>
        {sublabel ? (
          <Typography sx={{ color: t.muted, fontSize: 11 }}>
            {sublabel}
          </Typography>
        ) : null}
      </Box>
      {showDelta ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            color: positive ? t.green : t.red,
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {positive ? '▲' : '▼'}
          {Math.abs(delta)}%
        </Box>
      ) : null}
    </Box>
  );
}
